import React, { useRef, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import TinyMCEeditor from "../../components/const/TinyMCEeditor";

import {
  getUserTemplate,
  updateUserTemplate,
  deleteUserTemplate,
  saveUserTemplate,
} from "../../api/const";

import PageHeader from "../../components/common/ui/PageHeader";
import TableCell from "../../components/common/ui/TableCell";

export default function UserTemplateViewPage() {
  const { id, templateId } = useParams();
  const navigate = useNavigate();

  const isNew = !templateId; // 신규 여부
  const editorRef = useRef(null);

  const [editorKey, setEditorKey] = useState(0);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [periodType, setPeriodType] = useState("weekly");
  const [loading, setLoading] = useState(true);

  /** 기존 템플릿 로드 */
  useEffect(() => {
    const load = async () => {
      if (isNew) {
        setLoading(false);
        return;
      }

      try {
        const data = await getUserTemplate(templateId);
        setTitle(data.title ?? "");
        setContent(data.template ?? "");
        setPeriodType(data.period_type ?? "weekly");
        setEditorKey((k) => k + 1);
      } catch (err) {
        console.error("템플릿 로드 실패:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [templateId, isNew]);

  /** TinyMCE 초기화 후 내용 세팅 */
  useEffect(() => {
    if (editorRef.current && typeof content === "string") {
      editorRef.current.setContent(content);
    }
  }, [content]);

  /** 저장 */
  const handleSave = async () => {
    const html = editorRef.current?.getContent?.() ?? content ?? "";

    const body = {
      title,
      template: html,
      project: id,
      period_type: periodType,
    };

    try {
      if (isNew) {
        const created = await saveUserTemplate(body);
        alert("템플릿 생성 완료!");
        navigate(-1);

        if (created?.id) {
          navigate(`/project/${id}/const/userTemplate/view/${created.id}`, {
            replace: true,
          });
        } else {
          navigate(-1);
        }
      } else {
        await updateUserTemplate(templateId, body);
        alert("템플릿 수정 완료!");
        navigate(-1);
      }
    } catch (err) {
      console.error(err);
      alert("저장 실패");
    }
  };

  /** 삭제 */
  const handleDelete = async () => {
    if (!window.confirm("정말 삭제하시겠습니까?")) return;

    try {
      await deleteUserTemplate(templateId);
      alert("삭제 완료!");
      navigate(-1);
    } catch (err) {
      alert("삭제 실패");
    }
  };

  if (loading) return <div className="p-6">로딩 중...</div>;

  return (
    <div className="p-6 space-y-6">
      {/* 🔥 페이지 제목 */}
      <PageHeader>{isNew ? "새 템플릿 작성" : "템플릿 수정"}</PageHeader>

      {/* 페이지 본문 박스 */}
      <div className="page-box space-y-6">
        {/* ===================== 기본 정보 입력 ===================== */}
        <table className="w-full border text-sm">
          <colgroup>
            <col className="w-40" />
            <col />
          </colgroup>
          <tbody>
            {/* 제목 */}
            <tr className="divide-x">
              <TableCell
                label="템플릿 제목"
                editing
                input={
                  <input
                    type="text"
                    className="input-base"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                  />
                }
              />
            </tr>

            {/* 주간/월간 */}
            <tr className="divide-x">
              <TableCell
                label="보고 구분"
                editing
                input={
                  <select
                    value={periodType}
                    onChange={(e) => setPeriodType(e.target.value)}
                    className="input-base bg-white"
                  >
                    <option value="weekly">주간</option>
                    <option value="monthly">월간</option>
                  </select>
                }
              />
            </tr>

            {/* TinyMCE */}
            <tr className="divide-x">
              <TableCell
                label="템플릿 내용"
                editing
                rich
                input={
                  <TinyMCEeditor
                    key={editorKey}
                    editorRef={editorRef}
                    initialValue={content}
                    height={600}
                  />
                }
                valueBg="bg-white align-top"
              />
            </tr>
          </tbody>
        </table>

        {/* ===================== 버튼 ===================== */}
        <div className="flex gap-2">
          <button className="btn-save" onClick={handleSave}>
            저장
          </button>

          {!isNew && (
            <button className="btn-delete" onClick={handleDelete}>
              삭제
            </button>
          )}

          <button className="btn-basic" onClick={() => navigate(-1)}>
            돌아가기
          </button>
        </div>
      </div>
    </div>
  );
}
